import { useCallback, useState } from 'react';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type {
  ContactFormLeadTriggerObject,
  ContactFormTriggerLocation,
} from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import { emitTrackRequestContactStartEvent } from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import FullscreenOverlay from '../../../ui/FullscreenOverlay';
import { useDetailPanelSafe } from '../../../ui/DetailPanel';
import { HaloSpokeHeader } from '../HaloSpokeHeader';
import { ContactFormSpoke } from './ContactFormSpoke';
import { ContactFormSpokeContent } from './ContactFormSpokeContent';

/**
 * Hook that provides a handler to open the contact form from anywhere.
 *
 * Desktop (with DetailPanel): opens ContactFormSpokeContent in the DetailPanel.
 * Mobile / no DetailPanel: manages a FullscreenOverlay — caller must render `contactFormOverlay` in their JSX.
 *
 * Safe to use outside of a DetailPanelProvider (e.g., on the SRP's FeedView).
 *
 * When a listing is provided, it's forwarded to ContactFormSpoke so the form
 * doesn't depend on Redux's currentListingDetails (which may not be populated
 * outside the HDP).
 */
export const useContactFormAction = (
  listing?: ListingDetails,
  triggerLocation: ContactFormTriggerLocation = 'home_details_halo',
  triggerObject: ContactFormLeadTriggerObject = 'home_details_component|modal_contact_form',
) => {
  const detailPanel = useDetailPanelSafe();
  const [isOpen, setIsOpen] = useState(false);

  const useOverlay = !detailPanel || detailPanel.isMobile;

  const openContactForm = useCallback(() => {
    emitTrackRequestContactStartEvent({
      listing,
      triggerLocation,
      triggerObject,
    });

    if (detailPanel && !detailPanel.isMobile) {
      detailPanel.openPanel('contact-form', ContactFormSpokeContent, {
        config: { listing, triggerLocation, triggerObject },
        stacked: true,
      });
    } else {
      setIsOpen(true);
    }
  }, [detailPanel, listing, triggerLocation, triggerObject]);

  const contactFormOverlay = useOverlay ? (
    <FullscreenOverlay.Root open={isOpen} onOpenChange={setIsOpen}>
      <FullscreenOverlay.Portal>
        <FullscreenOverlay.Backdrop>
          <FullscreenOverlay.Content>
            <HaloSpokeHeader title="Contact property" />
            <FullscreenOverlay.Body padding="edge-to-edge">
              <ContactFormSpoke
                onClose={() => setIsOpen(false)}
                listing={listing}
                triggerLocation={triggerLocation}
                triggerObject={triggerObject}
              />
            </FullscreenOverlay.Body>
          </FullscreenOverlay.Content>
        </FullscreenOverlay.Backdrop>
      </FullscreenOverlay.Portal>
    </FullscreenOverlay.Root>
  ) : null;

  return { openContactForm, contactFormOverlay };
};
